import { useCallback, useMemo } from 'react';
import {
  getAllSegments,
  getClipFPS,
  getClipReadOnlyPlayerConfig,
  getIsDuplicateRecording,
  isNotFullLengthClip,
  isOriginalLayoutAvailable,
  isSplitsInTimelineEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { getDeletesForSegmentPure } from '@goldcast/player-config/src/utils/timeUtils';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/constants';
import { getEditsForSegment, getScenesForSegment } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/playerConfigUtil';
import {
  buildTrimmedClipForCreate,
  TrimmedClipForCreate
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/buildTrimmedClipForCreate';
import { hasCompositeSegmentBetweenSegmentIds } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/createClipValidation';
import { isAudioContent } from '@/stores/core';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { showErrorToast } from '@/libs/toast/toast';
import useTranscriptPage from '@/Pages/TranscriptPage/useTranscriptPage';

/** Preserve visual/layout metadata when creating a clip instead of falling back to API defaults. */
const getMetadataToPreserve = (metadata: Clip['asset_metadata']): Record<string, unknown> => {
  const values = {
    hide_borders: metadata.hide_borders,
    round_corners: metadata.round_corners,
    pip_shape: metadata.pip_shape,
    pip_position: metadata.pip_position,
    speaker_direction: metadata.speaker_direction,
    speaker_label_styles: metadata.speaker_label_styles,
    subtitle: metadata.subtitle,
    caption_styles: metadata.caption_styles,
    caption_positions: metadata.caption_positions,
    word_highlight_color: metadata.word_highlight_color,
    highlight_type: metadata.highlight_type,
    text_highlight_color: metadata.text_highlight_color,
    fit: metadata.fit,
    size: metadata.size,
    maxVideoDuration: metadata.maxVideoDuration,
    visible_speakers: metadata.visible_speakers,
    visible_face_ids: metadata.visible_face_ids,
    visible_video_ids: metadata.visible_video_ids,
    is_new_layouts_migrated: metadata.is_new_layouts_migrated,
    magicLayout: metadata.magicLayout
  };

  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined));
};

const useCreateClip = ({ clip }: { clip: Clip }) => {
  const { createClip } = useTranscriptPage();

  const isCreateClipAllowed = useMemo(() => {
    if (isNotFullLengthClip(clip) && !getIsDuplicateRecording(clip)) {
      return false;
    }
    return true;
  }, [clip]);

  const getDefaultLayout = useCallback(() => {
    if (isAudioContent()) {
      return 'AUDIOGRAM';
    }
    if (isOriginalLayoutAvailable(clip)) {
      return 'DEFAULT';
    }
    return 'GRID';
  }, [clip]);

  const onCreateClip = useCallback(
    (
      startTime: number | null,
      endTime: number | null,
      startSegmentId?: string,
      endSegmentId?: string,
      isDuplicate?: boolean,
      title?: string,
      isTimelineSelection?: boolean
    ) => {
      const isClipDuplicationEnabled = !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Clip_Duplication];
      const isSplitsEnabled = isSplitsInTimelineEnabledPure({
        clip: clip,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      });
      const hasScenes =
        isSplitsEnabled || (clip.asset_metadata.scenes || []).length > (isClipDuplicationEnabled ? 0 : 1);
      if (isSplitsEnabled && startSegmentId && endSegmentId) {
        const allSegments = getAllSegments(clip);
        const fps = getClipFPS(clip);

        if (startTime === null || endTime === null) {
          return Promise.reject(new Error('Start time or end time is null'));
        }

        const hasComposite = hasCompositeSegmentBetweenSegmentIds(allSegments, startSegmentId, endSegmentId);

        if (!hasComposite) {
          showErrorToast('At least one recording segment is required between the selected start and end time.');
          return Promise.reject(new Error('At least one composite segment is required'));
        }

        // Splits already supports a trimmed player_config today; this path is not gated.
        let trimmed: TrimmedClipForCreate | null;
        try {
          trimmed = buildTrimmedClipForCreate({
            clip,
            startTime,
            endTime,
            startSegmentId,
            endSegmentId,
            fps
          });
        } catch (err) {
          return Promise.reject(err);
        }
        if (!trimmed) {
          return Promise.reject(new Error('Unable to build a trimmed clip for the selected range'));
        }

        const additionalParams: Record<string, any> = {
          ...(trimmed.playerConfig ? { player_config: trimmed.playerConfig } : {}),
          layout: hasScenes ? clip.asset_metadata.layout : getDefaultLayout(),
          duration: trimmed.duration,
          start: trimmed.start,
          end: trimmed.end,
          captions: trimmed.captions,
          ...(isClipDuplicationEnabled ? getMetadataToPreserve(clip.asset_metadata) : {})
        };

        return createClip(trimmed.minStartTime, trimmed.maxEndTime, {
          edits: trimmed.edits,
          deletes: trimmed.deletes,
          scenes: undefined,
          fps,
          additionalParams,
          isDuplicate,
          title,
          isTimelineSelection
        });
      }

      if (startTime === null || endTime === null) {
        return Promise.reject(new Error('Start time or end time is null'));
      }

      let clipDataWithUpdatedLayout: Clip;
      try {
        clipDataWithUpdatedLayout = hasScenes
          ? clip
          : (updateClipData(
              {
                clipId: clip.id,
                actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
                layout: getDefaultLayout()
              },
              {
                skipStoreAndBackendUpdate: true
              }
            ) as Clip);
      } catch (err) {
        return Promise.reject(err);
      }

      const playerConfig = isDuplicate
        ? getClipReadOnlyPlayerConfig(clipDataWithUpdatedLayout)
        : buildTrimmedClipForCreate({
            clip,
            startTime,
            endTime,
            startSegmentId,
            endSegmentId,
            fps: getClipFPS(clip)
          })?.playerConfig;
      return createClip(startTime, endTime, {
        edits: getEditsForSegment(clipDataWithUpdatedLayout),
        deletes: getDeletesForSegmentPure({
          clip: clipDataWithUpdatedLayout,
          isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
        }),
        scenes: getScenesForSegment(clipDataWithUpdatedLayout),
        fps: getClipFPS(clip),
        additionalParams: {
          layout: hasScenes ? clipDataWithUpdatedLayout.asset_metadata.layout : getDefaultLayout(),
          ...(isClipDuplicationEnabled
            ? {
                ...getMetadataToPreserve(clipDataWithUpdatedLayout.asset_metadata),
                ...(playerConfig ? { player_config: playerConfig } : {}),
                captions: clipDataWithUpdatedLayout.asset_metadata.captions
              }
            : {}),
          intro: clipDataWithUpdatedLayout.asset_metadata.intro,
          introDuration: clipDataWithUpdatedLayout.asset_metadata.introDuration,
          outro: clipDataWithUpdatedLayout.asset_metadata.outro,
          outroDuration: clipDataWithUpdatedLayout.asset_metadata.outroDuration
        },
        isDuplicate,
        title,
        isTimelineSelection
      });
    },
    [clip, createClip, getDefaultLayout]
  );

  const onCreateDuplicate = useCallback(
    (name?: string) => {
      const allSegments = getAllSegments(clip);
      const isSplitsEnabled = isSplitsInTimelineEnabledPure({
        clip,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      });

      if (!isSplitsEnabled || allSegments.length === 0) {
        return onCreateClip(clip.asset_metadata.start, clip.asset_metadata.end, undefined, undefined, true, name);
      }

      const firstSegment = allSegments[0];
      const lastSegment = allSegments[allSegments.length - 1];
      if (
        (firstSegment.type !== SegmentType.COMPOSITE && firstSegment.type !== SegmentType.VIDEO) ||
        (lastSegment.type !== SegmentType.COMPOSITE && lastSegment.type !== SegmentType.VIDEO)
      ) {
        throw new Error('Duplicate needs composite or video segment in start and end');
      }
      const fps = getClipFPS(clip);
      const firstSegmentStartTime = (firstSegment.currentBounds?.fromFrame || 0) / fps;
      const lastSegmentEndTime =
        (lastSegment.currentBounds?.toFrame || lastSegment.toFrame - lastSegment.fromFrame) / fps;

      return onCreateClip(firstSegmentStartTime, lastSegmentEndTime, firstSegment.id, lastSegment.id, true, name);
    },
    [clip, onCreateClip]
  );

  return { isCreateClipAllowed, onCreateClip, onCreateDuplicate };
};

export default useCreateClip;
